/**
 * Крупный просмотр образца по наведению: справа от каталога «Отделки» — кусок стены
 * или обивки полтора метра шириной с этой картинкой в настоящую величину, название,
 * коллекция и раппорт. В карточке каталога миниатюра крошечная, а здесь видно, что
 * это за рисунок и какого он размера на стене.
 *
 * Рисуется в `body`: панель каталога обрезает всё, что выходит за её край.
 * Мимо указателя — не мешает вести мышь дальше по карточкам.
 */
import { createPortal } from "react-dom";
import { UIKit } from "@/shared/ui";
import type { FinishCard } from "../logic";

/** Сторона просмотра, px */
const PEEK_PX = 320;
/** Сколько миллиметров стены помещается в просмотр по ширине */
const PEEK_MM = 1500;
/** Отступ от края каталога и от краёв окна, px */
const GAP = 12;

export interface FinishPeekTarget {
	card: FinishCard & { preview: NonNullable<FinishCard["preview"]> };
	/** Правый край каталога и верх карточки — рядом с ними встаёт просмотр */
	left: number;
	top: number;
}

export const FinishPeek = ({ target }: { target: FinishPeekTarget }) => {
	const { card, left, top } = target;
	const { src, width, height } = card.preview;
	const scale = PEEK_PX / PEEK_MM;
	const maxTop = window.innerHeight - PEEK_PX - 96 - GAP;
	return createPortal(
		<div
			aria-hidden="true"
			className="pointer-events-none fixed z-50 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
			style={{ left: left + GAP, top: Math.max(GAP, Math.min(top, maxTop)), width: PEEK_PX }}
		>
			<UIKit.Text
				as="span"
				className="block"
				style={{
					...card.style,
					width: PEEK_PX,
					height: PEEK_PX,
					backgroundImage: `url(${src})`,
					backgroundSize: `${width * scale}px ${height * scale}px`,
				}}
			/>
			<div className="flex flex-col gap-0.5 px-3 py-2">
				<UIKit.Text className="truncate text-sm font-semibold text-ink">{card.name}</UIKit.Text>
				{card.collection && (
					<UIKit.Text className="truncate text-xs text-muted">{card.collection}</UIKit.Text>
				)}
				<UIKit.Text className="text-xs text-muted">
					Рисунок повторяется через {Math.round(width / 10)} × {Math.round(height / 10)} см ·
					показано {PEEK_MM / 1000} м
				</UIKit.Text>
			</div>
		</div>,
		document.body,
	);
};
